import {
  ArrowLeft,
  ArrowUp,
  AtSign,
  ChevronUp,
  Folder,
  Hammer,
  Mail,
  Menu,
  MessageCircle,
  Mic,
  Wand2,
  Phone,
  Plus,
  Workflow,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { StudioSkeletonBlock } from "./StudioSkeletonBlock";
import type { StudioPaneSkeletonVariant } from "./studio-pane-skeleton-types";

/*
 * Every shape here copies the live component's box model (see the comment on
 * each part). The CSS lives in studio-pane-skeleton.css and repeats the real
 * numbers on purpose: chunk fallbacks paint before the pane's own CSS loads.
 * When a real layout changes, change its part here in the same commit.
 */

const pct = (n: number) => `${Math.round(n * 100)}%`;

/** Sidebar / mobile search row: 36px search pill, filter, + circle (.studio-dm-sidebar-chrome). */
export function PaneSearchStrip({ filter = false }: { filter?: boolean } = {}) {
  return (
    <div className="studio-pane-skeleton-search">
      <StudioSkeletonBlock rounded="pill" className="studio-pane-skeleton-search-pill" />
      {filter ? (
        <StudioSkeletonBlock rounded="pill" className="studio-pane-skeleton-search-filter" />
      ) : null}
      <StudioSkeletonBlock rounded="circle" className="studio-pane-skeleton-head-icon" />
    </div>
  );
}

const DM_ROW_WIDTHS = [0.46, 0.62, 0.38, 0.54, 0.44, 0.58, 0.4, 0.5];

/** Chat list row (.studio-dm-row): filled tile, capsule left edge, 48px face, name + time, preview. */
export function DmListRows({ count = 8 }: { count?: number }) {
  return (
    <div className="studio-pane-skeleton-dm-list">
      {DM_ROW_WIDTHS.slice(0, count).map((width, index) => (
        <div className="studio-pane-skeleton-dm-row" key={index}>
          <StudioSkeletonBlock rounded="circle" className="studio-pane-skeleton-dm-avatar" />
          <div className="studio-pane-skeleton-dm-copy">
            <div className="studio-pane-skeleton-dm-top">
              <StudioSkeletonBlock rounded="row" className="is-name" style={{ width: pct(width) }} />
              <StudioSkeletonBlock rounded="row" className="is-time" />
            </div>
            <StudioSkeletonBlock rounded="row" className="is-preview" style={{ width: pct(Math.min(0.92, width + 0.3)) }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/*
 * Open chat thread (.studio-dm-messages > .studio-dm-message-block): received
 * bubbles on the left (20px radius, 8px 11px pad, up to 80% wide, name + time
 * line on the first bubble of a run), mine on the right in the accent fill.
 * The thread hugs the composer like the live chat scrolled to the bottom.
 */
const DM_THREAD_SHAPE: Array<{ mine: boolean; width: number; lines: number[] }> = [
  { mine: false, width: 0.8, lines: [0.94, 0.88, 0.42] },
  { mine: true, width: 0.36, lines: [0.86] },
  { mine: false, width: 0.8, lines: [0.96, 0.9, 0.93, 0.58] },
  { mine: false, width: 0.8, lines: [0.92, 0.95, 0.62] },
];

export function DmThreadRows() {
  return (
    <div className="studio-pane-skeleton-dm-thread">
      {DM_THREAD_SHAPE.map((msg, index) => (
        <div
          className={`studio-pane-skeleton-dm-msg${msg.mine ? " is-mine" : ""}`}
          key={index}
          style={{ width: pct(msg.width) }}
        >
          {msg.mine ? null : (
            <div className="studio-pane-skeleton-dm-msg-head">
              <StudioSkeletonBlock rounded="row" className="is-name" />
              <StudioSkeletonBlock rounded="row" className="is-time" />
            </div>
          )}
          {msg.lines.map((width, line) => (
            <span className="studio-pane-skeleton-dm-msg-line" key={line}>
              <StudioSkeletonBlock rounded="row" className="is-line" style={{ width: pct(width) }} />
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

/** 36px ringed circle with its glyph (.studio-composer-circle-btn). */
function RingCircle({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <span className={`studio-pane-skeleton-ring${className ? ` ${className}` : ""}`}>{children}</span>;
}

/** Open chat head (.studio-dm-chat-head): [back], peer pill (28px face + name), call + tools circles. */
function DmThreadHead({ back = false }: { back?: boolean }) {
  return (
    <div className={`studio-pane-skeleton-dm-head${back ? " has-back" : ""}`}>
      {back ? (
        <RingCircle className="is-head">
          <ArrowLeft aria-hidden="true" />
        </RingCircle>
      ) : null}
      <span className="studio-pane-skeleton-dm-peer">
        <StudioSkeletonBlock rounded="circle" className="studio-pane-skeleton-dm-peer-face" />
        <StudioSkeletonBlock rounded="row" className="is-name" />
      </span>
      <RingCircle className="is-head">
        <Phone aria-hidden="true" />
      </RingCircle>
      <RingCircle className="is-head">
        <Hammer aria-hidden="true" />
      </RingCircle>
    </div>
  );
}

/**
 * Composer (.studio-dm-composer-box): 20px-radius glass box with the soft
 * ring, a 40px message row (placeholder), then the toolbar row: + / @ / mode
 * pill, and enhance / mic / accent send on the right.
 */
function DmComposerStub() {
  return (
    <div className="studio-pane-skeleton-dm-composer">
      <div className="studio-pane-skeleton-dm-composer-box">
        <div className="studio-pane-skeleton-dm-composer-field">Type a message</div>
        <div className="studio-pane-skeleton-dm-composer-tools">
          <RingCircle><Plus aria-hidden="true" /></RingCircle>
          <RingCircle><AtSign aria-hidden="true" /></RingCircle>
          <RingCircle className="is-pill">
            <MessageCircle aria-hidden="true" />
            <ChevronUp aria-hidden="true" className="is-chevron" />
          </RingCircle>
          <span className="studio-pane-skeleton-dm-composer-gap" />
          <RingCircle><Wand2 aria-hidden="true" /></RingCircle>
          <RingCircle><Mic aria-hidden="true" /></RingCircle>
          <span className="studio-pane-skeleton-dm-composer-send"><ArrowUp aria-hidden="true" /></span>
        </div>
      </div>
    </div>
  );
}

/** Files grid (.cursor-file-grid > .desk-file-grid-item): square 24px-radius tiles, centred name. */
function FilesGrid({ count = 12 }: { count?: number }) {
  return (
    <div className="studio-pane-skeleton-files-grid">
      {Array.from({ length: count }, (_, index) => (
        <div className="studio-pane-skeleton-file-tile" key={index}>
          <StudioSkeletonBlock rounded="card" className="studio-pane-skeleton-file-thumb" />
          <StudioSkeletonBlock rounded="row" className="is-label" style={{ width: pct(0.42 + ((index * 7) % 5) * 0.08) }} />
        </div>
      ))}
    </div>
  );
}

/** Files list (.desk-file-list): column head, then icon + name + meta rows. */
function FilesList({ count = 9 }: { count?: number }) {
  return (
    <div className="studio-pane-skeleton-files-list">
      <div className="studio-pane-skeleton-files-list-head">
        <StudioSkeletonBlock rounded="row" className="is-label" style={{ width: "3rem" }} />
        <StudioSkeletonBlock rounded="row" className="is-label" style={{ width: "3.5rem" }} />
      </div>
      {Array.from({ length: count }, (_, index) => (
        <div className="studio-pane-skeleton-files-list-row" key={index}>
          <StudioSkeletonBlock rounded="card" className="studio-pane-skeleton-file-icon" />
          <StudioSkeletonBlock rounded="row" className="is-name" style={{ width: pct(0.3 + ((index * 5) % 4) * 0.08) }} />
          <StudioSkeletonBlock rounded="row" className="is-time" />
        </div>
      ))}
    </div>
  );
}

/** Mail inbox (.studio-mail-list > .studio-mail-row): filled 16px-radius row, 40px face, from / subject / snippet, time. */
function MailRows({ count = 7 }: { count?: number }) {
  return (
    <div className="studio-pane-skeleton-mail-list">
      {Array.from({ length: count }, (_, index) => (
        <div className="studio-pane-skeleton-mail-row" key={index}>
          <StudioSkeletonBlock rounded="circle" className="studio-pane-skeleton-mail-avatar" />
          <div className="studio-pane-skeleton-mail-copy">
            <StudioSkeletonBlock rounded="row" className="is-name" style={{ width: pct(0.24 + (index % 3) * 0.08) }} />
            <StudioSkeletonBlock rounded="row" className="is-line" style={{ width: pct(0.5 + (index % 4) * 0.08) }} />
            <StudioSkeletonBlock rounded="row" className="is-line is-faint" style={{ width: pct(0.72 + (index % 2) * 0.12) }} />
          </div>
          <StudioSkeletonBlock rounded="row" className="is-time" />
        </div>
      ))}
    </div>
  );
}

/** Workflow cards (.studio-workflows-list.is-cards): 22px card, 12px pad, 16/10 banner, title, two 22px pills. */
function WorkflowCards({ count = 6 }: { count?: number }) {
  return (
    <div className="studio-pane-skeleton-workflow-grid">
      {Array.from({ length: count }, (_, index) => (
        <div className="studio-pane-skeleton-workflow-card" key={index}>
          <StudioSkeletonBlock rounded="card" className="studio-pane-skeleton-workflow-banner" />
          <div className="studio-pane-skeleton-workflow-body">
            <StudioSkeletonBlock rounded="row" className="is-name" style={{ width: pct(0.5 + (index % 3) * 0.12) }} />
            <div className="studio-pane-skeleton-workflow-pills">
              <StudioSkeletonBlock rounded="pill" className="studio-pane-skeleton-workflow-pill" />
              <StudioSkeletonBlock rounded="pill" className="studio-pane-skeleton-workflow-pill is-wide" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** To-do "Now" list (CursorTable): header row + body rows (Task, Run, Who, Opened). */
function TodoTable({ count = 6 }: { count?: number }) {
  const cols = [0.36, 0.26, 0.18, 0.12];
  return (
    <div className="studio-pane-skeleton-table">
      <div className="studio-pane-skeleton-table-row is-head">
        {cols.map((w, i) => (
          <StudioSkeletonBlock rounded="row" className="is-label" key={i} style={{ width: pct(w * 0.5) }} />
        ))}
      </div>
      {Array.from({ length: count }, (_, index) => (
        <div className="studio-pane-skeleton-table-row" key={index}>
          {cols.map((w, i) => (
            <StudioSkeletonBlock
              rounded="row"
              className="is-line"
              key={i}
              style={{ width: pct(w * (0.6 + ((index + i) % 3) * 0.15)) }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Generic pane head row: title pill left, tool circle right (StudioPanelHead). */
function PaneHeadStub({ title = 0.32 }: { title?: number }) {
  return (
    <div className="studio-pane-skeleton-pane-head">
      <StudioSkeletonBlock rounded="pill" style={{ width: pct(title) }} />
      <StudioSkeletonBlock rounded="circle" className="studio-pane-skeleton-head-icon" />
    </div>
  );
}

/** Run screen (RunScreen): main (head + plate) and, on wide panes, the right rail (tabs + steps). */
function RunPane() {
  return (
    <div className="studio-pane-skeleton-split">
      <div className="studio-pane-skeleton-split-main">
        <PaneHeadStub title={0.38} />
        <StudioSkeletonBlock rounded="card" className="studio-pane-skeleton-run-plate" />
      </div>
      <div className="studio-pane-skeleton-split-rail">
        <StudioSkeletonBlock rounded="pill" className="studio-pane-skeleton-rail-tabs" />
        {[0.62, 0.48, 0.7, 0.54, 0.44].map((w, i) => (
          <div className="studio-pane-skeleton-step-row" key={i}>
            <StudioSkeletonBlock rounded="card" className="studio-pane-skeleton-step-kind" />
            <StudioSkeletonBlock rounded="row" className="is-name" style={{ width: pct(w) }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Video editor (StudioVideoEditor): preview over transport + tracks, inspector dock right. */
function EditorPane() {
  return (
    <div className="studio-pane-skeleton-split is-editor">
      <div className="studio-pane-skeleton-split-main">
        <StudioSkeletonBlock rounded="card" className="studio-pane-skeleton-editor-preview" />
        <StudioSkeletonBlock rounded="pill" className="studio-pane-skeleton-editor-transport" />
        {[0.82, 0.64, 0.4].map((w, i) => (
          <StudioSkeletonBlock rounded="card" className="studio-pane-skeleton-editor-track" key={i} style={{ width: pct(w) }} />
        ))}
      </div>
      <div className="studio-pane-skeleton-split-rail">
        <StudioSkeletonBlock rounded="pill" className="studio-pane-skeleton-rail-tabs" />
        <StudioSkeletonBlock rounded="card" className="studio-pane-skeleton-rail-card" />
      </div>
    </div>
  );
}

export function PaneVariantBody({
  variant,
  main,
  isMobile = false,
}: {
  variant: StudioPaneSkeletonVariant;
  main?: ReactNode;
  isMobile?: boolean;
}) {
  if (main !== undefined) {
    return <div className="studio-pane-skeleton-body">{main}</div>;
  }

  switch (variant) {
    case "dm-thread":
      return <DmThreadRows />;
    case "messages-thread":
      return (
        <div className="studio-pane-skeleton-body is-flush is-thread">
          <DmThreadHead back={isMobile} />
          <div className="studio-pane-skeleton-dm-scroll">
            <DmThreadRows />
          </div>
          <DmComposerStub />
        </div>
      );
    case "dm-list":
      return <DmListRows />;
    case "dm-sidebar":
      return (
        <div className="studio-pane-skeleton-body is-flush">
          <PaneSearchStrip filter />
          <DmListRows />
        </div>
      );
    case "messages-home":
      // Desktop: the chat list lives in the rail; the main pane is the quiet
      // "pick a chat" state. Phone: the pane IS the chat list.
      return isMobile ? (
        <div className="studio-pane-skeleton-body is-flush">
          <PaneSearchStrip filter />
          <DmListRows />
        </div>
      ) : (
        <div className="studio-pane-skeleton-body is-flush" />
      );
    case "workflows-home":
      return <WorkflowCards />;
    case "workflows-detail":
      return (
        <div className="studio-pane-skeleton-body">
          <PaneHeadStub title={0.42} />
          <StudioSkeletonBlock rounded="card" className="studio-pane-skeleton-fill-card" />
        </div>
      );
    case "todo":
      return <TodoTable />;
    case "files":
      return <FilesGrid />;
    case "files-list":
      return <FilesList />;
    case "mail":
      return <MailRows />;
    case "run":
      return <RunPane />;
    case "editor":
      return <EditorPane />;
    case "boot-messages":
      // Phone boot frame (studio-skeleton-chrome): the Messages list.
      return (
        <div className="studio-pane-skeleton-body is-flush">
          <PaneSearchStrip filter />
          <DmListRows />
        </div>
      );
    case "generic-center":
    default:
      // Unknown pane (profile, academy, canvas, post…): neutral head + body,
      // never a chat list.
      return (
        <div className="studio-pane-skeleton-body">
          <PaneHeadStub />
          <StudioSkeletonBlock rounded="card" className="studio-pane-skeleton-fill-card" />
        </div>
      );
  }
}

/** `selected={false}`: no item highlighted (signed-out sign-in page). */
export function ShellMobileNavStub({ selected = true }: { selected?: boolean } = {}) {
  return (
    <nav className="studio-mobile-bottom-nav studio-pane-skeleton-shell-nav-stub" aria-hidden="true">
      <div className="studio-mobile-nav-sections">
        <span className="studio-mobile-nav-btn is-icon-only"><Folder aria-hidden="true" /></span>
        <span className={`studio-mobile-nav-btn is-icon-only${selected ? " is-active" : ""}`}><MessageCircle aria-hidden="true" /></span>
        <span className="studio-mobile-nav-btn is-icon-only"><Mail aria-hidden="true" /></span>
        <span className="studio-mobile-nav-btn is-icon-only"><Workflow aria-hidden="true" /></span>
      </div>
      <div className="studio-mobile-nav-tools">
        <span className="studio-mobile-nav-btn is-icon-only is-tool"><Menu aria-hidden="true" /></span>
      </div>
    </nav>
  );
}

/** Phone tab strip. `tab`: one active tab with that title (boot frame, default landing). */
export function ShellHeadStub({ tab }: { tab?: string } = {}) {
  return (
    <header className="studio-desktop-workspace-head cursor-workspace-head" aria-hidden="true">
      <div className="studio-head-nav-pill">
        <div className="cursor-unified-tabs studio-pane-skeleton-shell-head-tabs">
          <div className="cursor-unified-tabs-scroll">
            {tab ? (
              <span className="studio-pane-skeleton-shell-tab">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <span className="studio-pane-skeleton-shell-tab-label">{tab}</span>
                <span className="studio-pane-skeleton-shell-tab-x">
                  <X aria-hidden="true" />
                </span>
              </span>
            ) : (
              <>
                <StudioSkeletonBlock rounded="pill" style={{ width: "7.5rem" }} />
                <StudioSkeletonBlock rounded="pill" style={{ width: "6.25rem" }} />
                <StudioSkeletonBlock rounded="pill" style={{ width: "6.75rem" }} />
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
